import { Component, FocusEventHandler, FormEventHandler, KeyboardEventHandler, FC, ReactNode } from 'react'
import * as styles from 'styles/atoms/inputs/TextField.css'
import * as cn from 'classnames'
import { generate } from 'randomstring'
import { ErrorItem, InputTheme, QAMarked } from 'interfaces/component'
import { TooltipIcon, TooltipIconShape } from 'components/features/pfm/Tooltip/TooltipIcon'
import { MaskedInput } from 'react-text-mask'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import withMobile, { WithMobileProps } from 'hocs/withMobile/withMobile'
import resolveErrorById from 'constants/errors'

type TextFieldEllipsisMode = 'show' | 'none-focus' | 'none'

export type TextFieldOwnProps = QAMarked & {
  active?: boolean
  autoComplete?: string
  autoFocus?: boolean
  disabled?: boolean
  ellipsis?: TextFieldEllipsisMode
  ellipsisTheme?: InputTheme
  errored?: boolean // need to display error styles
  errors?: ErrorItem[] // list of errors to display
  showLabelAsLink?: boolean
  hint?: string
  hintIcon?: TooltipIconShape
  isLoading?: boolean
  keepCharPositions?: boolean
  label?: string
  mask?: string[]
  maxLength?: number
  pipe?: any
  placeholder?: string
  readOnly?: boolean
  touched?: boolean
  type?: TextFieldType
  submitFailed?: boolean
  value: string
  color?: 'red' | 'yellow' | 'green'
  innerClassname?: string
  spellCheck?: boolean

  onFocus?: FocusEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange?: FormEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
}

export type TextFieldState = {
  id: string
}

export type TextFieldType = 'text' | 'text-secure' | 'password' | 'email' | 'tel'

type TextFieldProps = TextFieldOwnProps
type TextFieldDefaultProps = TextFieldProps & typeof TextFieldComponent.defaultProps & { children?: ReactNode }

/**
 * Children will used as decorating elements
 *
 * @deprecated - Use LabeledInput / react-hook-form
 */
export class TextFieldComponent extends Component<TextFieldProps, TextFieldState> {
  static defaultProps = {
    autoFocus: false,
    ellipsis: 'none',
    ellipsisTheme: 'white',
    hintIcon: 'question',
    errors: [] as ErrorItem[],
  }

  state = {
    id: '',
  }

  input: HTMLInputElement | null

  private get isActive () {
    const { autoFocus, touched, active } = this.props

    return (autoFocus && !touched) || active
  }

  constructor (props: TextFieldProps, context: any) {
    super(props, context)

    this.state = {
      ...this.state,
      id: generate(),
    }
  }

  render () {
    const {
      mask, pipe, keepCharPositions,
      onFocus, onBlur, onKeyDown,
      value, disabled, placeholder,
      autoComplete, isLoading,
      autoFocus, maxLength, errors,
      touched, label, errored, hint, hintIcon,
      submitFailed, showLabelAsLink,
      readOnly, ellipsis, ellipsisTheme,
      qaMarker, qaType,
      color,
      innerClassname,
      spellCheck,

      children,
    } = this.props as TextFieldDefaultProps

    const typeProp = this.props.type || 'text'
    const isTestSecure = typeProp === 'text-secure'
    const type = isTestSecure ? 'text' : typeProp

    const inputProps = {
      className: cn(styles.input, isTestSecure && styles.textSecurity),
      id: this.state['id'],
      onChange: this.onChange,
      value,
      type,
      placeholder,
      onFocus,
      onBlur,
      onKeyDown,
      disabled,
      mask,
      pipe,
      keepCharPositions,
      autoComplete,
      autoFocus,
      maxLength,
      readOnly,
      spellCheck,
    }

    if (!this.isActive || !placeholder) {
      delete inputProps.placeholder
    }

    if (!inputProps.mask) {
      delete inputProps.pipe
      delete inputProps.keepCharPositions
    }

    if (inputProps.autoComplete === 'off') {
      // Fix autocomplete='off' ignored by Chrome
      // Since this random value is not a valid one, the browser will give up
      // (https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion)
      inputProps.autoComplete = 'new-password'
    }

    const hasErrors = errors.length > 0
    const shouldShowErrors = !disabled && ((touched || submitFailed) && hasErrors)

    const tooltipIcon = hint ? (
      <div className={ styles.tooltipIconWrapper }>
        <TooltipIcon icon={ hintIcon }>{ hint }</TooltipIcon>
      </div>
    ) : null

    const fieldLabel = label ? (
      <label className={ styles.label } data-qa-type='label' htmlFor={ this.state['id'] }>
        <div className={ styles.labelText } data-qa-type='label-text'>{ label }</div>
        { tooltipIcon }
      </label>
    ) : null

    const fieldInput = mask ? (
      <MaskedInput
        { ...inputProps }
        mask={ mask }
        ref={ (input: any) => { this.input = input && input.inputElement } }
        data-qa-type='input'
      />
    ) : (
      <input
        { ...inputProps }
        ref={ (input: HTMLInputElement | null) => { this.input = input } }
        data-qa-type='input'
      />
    )

    const errorsClass = cn(styles.errorsWrapper, {
      [styles.singleError]: errors.length === 1,
      [styles.multipleErrors]: errors.length > 1,
    })

    const errorsBlock = shouldShowErrors ? (
      <div className={ errorsClass } data-qa-type='error-list'>
        <ul className={ styles.errorList }>
          {
            errors.map((item, i) => (
              <li
                key={ `error-${ i }` }
                className={ styles.errorListItem }
                data-qa-type='error-list-item'
              >
                { resolveErrorById(item.text as any) }
              </li>
            ))
          }
        </ul>
      </div>
    ) : null

    const withValue = typeof value !== 'undefined' && value !== null && String(value).length > 0
    const colorClass = color ? styles[color] : undefined

    const textboxInnerClass = cn(styles.textboxInner, colorClass, innerClassname, {
      [styles.active]: this.isActive,
      [styles.showLabelAsLink]: showLabelAsLink,
      [styles.withValue]: this.isActive || withValue,
      [styles.error]: !disabled && (hasErrors || errored),
      [styles.disabled]: Boolean(disabled),
    })

    const inputWrapperClassName = cn(styles.inputWrapper, {
      [styles.withEllipsis]: ellipsis === 'show' || (ellipsis === 'none-focus' && !this.isActive),
      [styles.ellipsisGray]: ellipsisTheme === 'gray',
      [styles.ellipsisSnow]: ellipsisTheme === 'snow',
      [styles.ellipsisDark]: ellipsisTheme === 'dark',
    })

    return (
      <div className={ styles.textbox } data-qa-type={ qaType || type } data-qa-marker={ qaMarker }>
        <div className={ textboxInnerClass }>
          <div className={ inputWrapperClassName }>
            { fieldInput }
          </div>
          { children && <div className={ styles.decoration }>{ children }</div> }
          <div className={ styles.labelWrapper }>
            { fieldLabel }
          </div>
          { isLoading && <div className={ styles.textBoxSpinner }><Spinner /></div> }
        </div>
        { errorsBlock }
      </div>
    )
  }

  componentDidMount () {
    this.setFocus()
  }

  componentDidUpdate (prevProps: TextFieldProps) {
    if (!prevProps.active && this.props.active) {
      this.setFocus()
    } else if (prevProps.active && !this.props.active) {
      this.blur()
    }
  }

  public focus () {
    if (this.input) {
      this.input.focus()
    }
  }

  private blur () {
    if (this.input) {
      this.input.blur()
    }
  }

  private setFocus () {
    if (this.input && this.isActive) {
      this.input.focus()
    }
  }

  private onChange: FormEventHandler<HTMLInputElement> = (e) => {
    const { onChange } = this.props

    onChange && onChange(e)
  }
}

type SaturatedTextFieldWrapperProps =
  & TextFieldProps
  & WithMobileProps & {
    innerRef?: (el: TextFieldComponent) => void
    forceAutoFocus?: boolean
  }
/**
 * @deprecated - Use LabeledInput / react-hook-form
 */
const SaturatedTextFieldWrapper: FC<SaturatedTextFieldWrapperProps> = (props) => {
  // turn off autofocus on mobiles
  const autoFocus = !props.isMobile && props.autoFocus || !!props.forceAutoFocus
  const { innerRef, ...restProps } = props

  return (
    <TextFieldComponent { ...restProps } ref={ innerRef } autoFocus={ autoFocus } />
  )
}

/**
 * @deprecated - Use LabeledInput / react-hook-form
 */
const TextField = withMobile<SaturatedTextFieldWrapperProps>(SaturatedTextFieldWrapper)

export default TextField
